import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';

import App from './App';
import { initI18n } from './i18n/init';
import { initCountly, parameterizePath } from './lib/countly';
import { installDevTools } from './lib/devTools';
import { initTheme } from './lib/theme';
import { config } from './lib/config';
import './index.css';

// Apply the user's stored theme BEFORE React renders so the first paint
// already shows the right palette — otherwise the page briefly flashes
// the default tokens before the React tree mounts and switches.
initTheme();
initI18n();
initCountly();
installDevTools();

if (config.SENTRY_DSN) {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    tunnel: config.SENTRY_TUNNEL || undefined,
    environment: config.IA_ENV,
    release: config.RELEASE,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      // OWASP A02 — strip request bodies/responses to avoid forwarding PII to Sentry.
      if (event.request) {
        delete event.request.data;
        // The URL embeds patient ids (/patients/12345/...) — parameterize it.
        if (event.request.url) {
          event.request.url = parameterizePath(event.request.url);
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Sentry auto-captures navigation + fetch/xhr breadcrumbs whose URLs
      // embed patient ids (e.g. /patients/12345/meals). Scrub those numeric
      // segments the same way Countly view tracking does before the trail
      // is attached to any event.
      if (typeof breadcrumb.data?.url === 'string') {
        breadcrumb.data.url = parameterizePath(breadcrumb.data.url);
      }
      if (typeof breadcrumb.message === 'string') {
        breadcrumb.message = parameterizePath(breadcrumb.message);
      }
      return breadcrumb;
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Keep dashboard data warm across tab switches and patient revisits so the
      // Summary widgets (Average Plate, Med Score, CGM, macronutrients…) render
      // instantly from cache instead of re-showing skeletons every visit.
      //
      // Every patient tab unmounts on navigation (PatientShell route-swaps them),
      // so the widgets' queries lose their observers each time you leave Summary.
      //   • staleTime — how long cached data is served before a background
      //     refetch. 30s was short enough that almost every remount refetched
      //     (flicker + network churn).
      //   • gcTime — how long an UNOBSERVED cache entry survives before it's
      //     dropped. It was unset, so React Query's 5-minute default applied:
      //     leave Summary for >5 min and the cache is gone → cold, skeleton
      //     reload on return. This was the main cause of the "reloads every
      //     visit" report.
      //
      // Clinician data doesn't change second-to-second, and mutations still call
      // invalidateQueries() to force an immediate refetch, so a few minutes of
      // staleness is safe. Per-query overrides (e.g. useDisaggregatedNutrition)
      // still win where a different window is wanted.
      staleTime: 5 * 60_000, // 5 minutes
      gcTime: 30 * 60_000, // 30 minutes
    },
  },
});

const Root = () => (
  <Sentry.ErrorBoundary fallback={<div className="p-8">Something went wrong.</div>}>
    <QueryClientProvider client={queryClient}>
      {/* Opt into React Router v7 behaviors so the v6 deprecation warnings
          (`v7_startTransition`, `v7_relativeSplatPath`) stop logging on
          every render. We're already compatible with both behaviors —
          this just silences the noise without changing app behavior. */}
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <App />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

// Vite's HMR can re-execute main.tsx during a hot update, which calls
// `createRoot` a second time on the same DOM node — React 18 logs:
//   "You are calling ReactDOMClient.createRoot() on a container that has
//    already been passed to createRoot() before."
// Stash the root on the container so subsequent runs reuse it.
const container = document.getElementById('root')! as HTMLElement & {
  __rxRoot?: ReactDOM.Root;
};
const root = container.__rxRoot ?? ReactDOM.createRoot(container);
container.__rxRoot = root;
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
